/**
 * LockedMetricToken (metric-tiering slice) — the per-value lock / teaser chip.
 * ===========================================================================
 * For DEMO users the backend keeps base performance exact but withholds premium
 * measured metrics (impedance, thermal, batch & aging, DC25). Wherever such a
 * value would render, we draw this token instead of a bare "—" (which reads as
 * "Batemo has no data" — trust death for a B2B measured-data product).
 *
 * Two shapes:
 *   - band  → a coarse teaser chip ("Low"/"Medium"/"High") for the quality
 *             bands (batch spread, post-formation loss). Communicates the
 *             ballpark while keeping the exact number PRO.
 *   - gated → a "PRO" lock chip that keeps the UNIT visible so the row still
 *             reads as a real measured metric, just locked.
 *
 * Clicking either records the conversion signal + opens the category-framed
 * upsell (first click of the session = modal, later = slim toast). It never
 * renders a plain "—". That `onActivate` behaviour is ALWAYS the PRO-upsell
 * one — it never changes based on tier. Some call sites (Grid/Table) wrap
 * the token in their own `onClickCapture` that intercepts the click before
 * it reaches `onActivate` and routes it to a real Deep-Dive unlock instead.
 * Those call sites pass `unlockAction="deep-dive"` purely to swap the LABEL
 * so the chip doesn't lie about what the click will do — it does not change
 * the click behaviour itself (that's the caller's job).
 *
 * Usage:
 *   <LockedMetricToken band="high" label="Spread" category="quality" />
 *   <LockedMetricToken gated unit="mΩ" category="electrical" />
 *   <LockedMetricToken gated unit="mΩ" category="electrical" unlockAction="deep-dive" />
 */
import { trackEvent } from '../../modules/dataService.js'
import { recordMetricLocked } from '../../services/demoLimitService.js'
import { openMetricUpsell } from '../../state/demoLimitStore.js'

// Human labels for the coarse teaser bands.
const BAND_LABELS = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
}

// Small padlock — matches the lock vocabulary used on locked cards/rows.
function LockGlyph({ size = 11 }) {
  return (
    <svg
      className="locked-metric__lock"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      aria-hidden="true"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  )
}

/**
 * @param {object}  props
 * @param {string}  [props.band]     - 'low' | 'medium' | 'high' (teaser mode)
 * @param {boolean} [props.gated]    - true → PRO lock chip (locked-value mode)
 * @param {string}  [props.category] - metric category for the upsell/telemetry
 *                                      ('electrical' | 'thermal' | 'quality' | …)
 * @param {string}  [props.label]    - short label shown before the band chip
 * @param {string}  [props.unit]     - unit kept visible on the lock chip
 * @param {string}  [props.title]    - optional tooltip override
 * @param {string}  [props.unlockAction] - 'pro' (default) → real PRO-upsell
 *                                      wording, byte-identical to before this
 *                                      changed. 'deep-dive' → ONLY for call
 *                                      sites that themselves intercept the
 *                                      click and route it to a real Deep-Dive
 *                                      unlock (Grid/Table); shows "Unlock" +
 *                                      a visible "1 deep dive" cost hint.
 */
export default function LockedMetricToken({
  band = null,
  gated = false,
  category = 'metrics',
  label = null,
  unit = null,
  title = null,
  unlockAction = 'pro',
}) {
  const normalizedBand = band ? String(band).toLowerCase() : null
  const isBand = !!(normalizedBand && BAND_LABELS[normalizedBand])

  function onActivate(e) {
    e.stopPropagation() // never trigger the surrounding cell/row click
    recordMetricLocked(category)
    openMetricUpsell(category)
    trackEvent('Demo Metric Locked', { category })
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onActivate(e)
    }
  }

  const commonProps = {
    role: 'button',
    tabIndex: 0,
    onClick: onActivate,
    onKeyDown,
  }

  if (isBand) {
    const bandLabel = BAND_LABELS[normalizedBand]
    return (
      <span
        className={`locked-metric locked-metric--band locked-metric--band-${normalizedBand}`}
        title={title || `${label ? label + ': ' : ''}${bandLabel} — unlock the exact value with PRO`}
        aria-label={`${label ? label + ' ' : ''}${bandLabel}, PRO metric — unlock the exact value`}
        {...commonProps}
      >
        {label && <span className="locked-metric__label">{label}</span>}
        <span className="locked-metric__band-value">{bandLabel}</span>
        <LockGlyph />
      </span>
    )
  }

  // Gated (locked-value) chip — keep the unit visible so the metric still reads
  // as real measured data, just gated. The wording is caller-controlled via
  // `unlockAction`, NOT derived from tier state here: only call sites that
  // themselves intercept the click and route it to a real Deep-Dive unlock
  // (Grid/Table's onClickCapture → unlockCell) may pass "deep-dive". Every
  // other caller (Compare, cell-detail Quality, the "+N more" cue) relies on
  // this component's own onActivate, which is ALWAYS the PRO-upsell — so it
  // must keep the "PRO" wording regardless of the viewer's tier. Branching
  // this on isDemoCatalogUnlocked() here (as before) bled the "deep dive"
  // wording into those PRO-upsell surfaces too, promising an unlock the
  // click never performs — that's the bug this prop fixes.
  const isDeepDive = unlockAction === 'deep-dive'
  return (
    <span
      className="locked-metric locked-metric--gated"
      title={
        title ||
        (isDeepDive
          ? 'Unlock this cell to see the exact value — costs 1 deep dive'
          : 'Measured for every cell — unlock the exact value with PRO')
      }
      aria-label={
        isDeepDive
          ? `Unlock${unit ? ' ' + unit : ''} value — 1 deep dive`
          : `PRO metric${unit ? ' in ' + unit : ''} — unlock the exact value`
      }
      {...commonProps}
    >
      <LockGlyph />
      <span className="locked-metric__pro">{isDeepDive ? 'Unlock' : 'PRO'}</span>
      {/* Visible cost hint (not just title/aria — those are invisible on
          touch and need a hover on desktop). Compact by design. */}
      {isDeepDive && <span className="locked-metric__cost">· 1 deep dive</span>}
      {unit && <span className="locked-metric__unit">{unit}</span>}
    </span>
  )
}
